import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '6xl' | 'full';
  maxHeight?: string;
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
  headerActions?: ReactNode;
  children: ReactNode;
  className?: string;
  zIndex?: number;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  maxHeight = 'max-h-[90vh]',
  closeOnBackdropClick = false,
  showCloseButton = true,
  headerActions,
  children,
  zIndex = 9999,
  className = '',
}: ModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // ESC 키 핸들링
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // 스크롤 방지
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // 모달 애니메이션 처리
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때
      setIsVisible(true);
      // 다음 프레임에서 애니메이션 시작
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // 모달이 닫힐 때
      setIsAnimating(false);
      // 애니메이션 완료 후 DOM에서 제거
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300); // transition duration과 일치
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    full: 'max-w-full mx-4',
  };

  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      onClose();
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // 모달 내부 클릭 시 이벤트 전파 방지
    e.stopPropagation();
  };

  return (
    <div
      className={classNames(
        `fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[${zIndex}] transition-opacity duration-300 overflow-hidden`,
        isAnimating ? 'opacity-100' : 'opacity-0'
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white rounded-xl w-full ${sizeClasses[size]} ${maxHeight} overflow-hidden transition-all duration-300 ease-out ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        } ${className}`}
        onClick={handleModalClick}
      >
        {/* Header */}
        {(title || subtitle || showCloseButton || headerActions) && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex-1">
              {title && (
                <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              )}
              {subtitle && (
                <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {headerActions}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer p-1"
                  title="닫기"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`max-h-[calc(90vh-100px)] overflow-y-auto`}>
          {children}
        </div>
      </div>
    </div>
  );
}
